import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiClient } from "~/api/ApiClient";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { DeleteDashboardModal } from "./DeleteDashboardModal";

interface UseDeleteDashboardProps {
  siteId: number;
  dashboardId: number;
}

export const useDeleteDashboard = ({
  siteId,
  dashboardId,
}: UseDeleteDashboardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteDashboardAsync, isPending: isDeletingDashboard } =
    useMutation({
      mutationFn: async () => {
        await apiClient.sites.dashboardsDelete(siteId, dashboardId);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
          }),
          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.DASHBOARDS(siteId),
          }),
        ]);
      },
    });

  const onDelete = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleConfirm = async () => {
    await deleteDashboardAsync();
    setIsModalOpen(false);
  };

  const ConnectedDeleteModal = () => (
    <DeleteDashboardModal
      isOpen={isModalOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      isDeleting={isDeletingDashboard}
    />
  );

  return {
    onDelete,
    DeleteModal: ConnectedDeleteModal,
  };
};
